import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Tabs,
  Tab,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import QrCode2Icon from '@mui/icons-material/QrCode2';
import { useLayoutPreferences } from '../../../../context/LayoutPreferencesContext';

const BARCODE_FORMATS = [
  { value: 'text',               label: 'Texte',                    icon: <TextFieldsIcon sx={{ fontSize: '1.1rem' }} /> },
  { value: 'barcode_ean',        label: 'Code-barre avec EAN',      icon: <QrCode2Icon sx={{ fontSize: '1.1rem' }} /> },
  { value: 'barcode_no_ean',     label: 'Code-barre sans EAN',      icon: <QrCode2Icon sx={{ fontSize: '1.1rem' }} /> },
  { value: 'small_barcode_ean',  label: 'Petit code-barre avec EAN',icon: <QrCode2Icon sx={{ fontSize: '1.1rem' }} /> },
  { value: 'small_barcode_no_ean', label: 'Petit code-barre sans EAN', icon: <QrCode2Icon sx={{ fontSize: '1.1rem' }} /> },
] as const;

type BarcodeFormat = (typeof BARCODE_FORMATS)[number]['value'];

export interface BarcodeResult {
  snippet: string;
  isImage: boolean;
  imageUrl?: string;
  token: string;
}

function buildOfferResult(offerNum: string, format: BarcodeFormat): BarcodeResult {
  const token = `#NUM_COUPON_${offerNum}#`;
  const imageUrls: Partial<Record<BarcodeFormat, string>> = {
    barcode_ean:        '/images/common/barcode.png',
    barcode_no_ean:     '/images/common/barcode_no_ean.png',
    small_barcode_ean:  '/images/common/barcode_small.png',
    small_barcode_no_ean: '/images/common/barcode_small_no_ean.png',
  };
  const imageUrl = imageUrls[format];
  const isImage = !!imageUrl;
  const snippet = isImage
    ? `<img alt="${token}" src="${imageUrl}" style="max-width:${format.startsWith('small') ? 120 : 220}px;" />`
    : token;
  return { snippet, isImage, imageUrl, token };
}

interface InsertBarcodeDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (result: BarcodeResult) => void;
  offerCount?: number;
  offerTitles?: string[];
}

function InsertBarcodeDialog({ open, onClose, onConfirm, offerCount = 1, offerTitles }: InsertBarcodeDialogProps) {
  const { sidebarColor } = useLayoutPreferences();
  const [tab, setTab] = useState(0);
  const [selectedOffer, setSelectedOffer] = useState('0');
  const [barcodeFormat, setBarcodeFormat] = useState<BarcodeFormat>('barcode_ean');

  const existingCount = Math.max(1, offerCount);
  const offers = Array.from({ length: existingCount + 1 }, (_, i) => ({
    value: String(i),
    label: i < existingCount
      ? (offerTitles?.[i]?.trim() ? `Offre ${i + 1} — ${offerTitles[i]}` : `Offre ${i + 1}`)
      : `+ Nouvelle offre (Offre ${i + 1})`,
  }));

  const handleConfirm = () => {
    if (tab === 0) {
      onConfirm(buildOfferResult(selectedOffer, barcodeFormat));
    } else {
      onConfirm({ snippet: '#NUM_CARTE#', isImage: false, token: '#NUM_CARTE#' });
    }
    onClose();
  };

  const tabSx = {
    '& .MuiTabs-indicator': { backgroundColor: sidebarColor },
    '& .MuiTab-root.Mui-selected': { color: sidebarColor },
    '& .MuiTab-root': { textTransform: 'none', fontWeight: 500, fontSize: '0.85rem' },
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 1.75, px: 3, backgroundColor: sidebarColor }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ color: '#fff', fontSize: '0.95rem' }}>
          Code opération
        </Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: '#fff', opacity: 0.85, '&:hover': { opacity: 1 } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: '1px solid #dee2e6' }}>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={tabSx}>
          <Tab label="Codes opération" />
          <Tab label="Codes cartes" />
        </Tabs>
      </Box>

      <DialogContent sx={{ pt: 2.5, px: 3 }}>
        {tab === 0 && (
          <Box>
            <FormControl size="small" fullWidth sx={{ mb: 2.5 }}>
              <InputLabel sx={{ '&.Mui-focused': { color: sidebarColor } }}>Numéro de l'offre</InputLabel>
              <Select
                value={selectedOffer}
                label="Numéro de l'offre"
                onChange={e => setSelectedOffer(e.target.value)}
                sx={{ borderRadius: 1.5, '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: sidebarColor } }}
              >
                {offers.map(o => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
              </Select>
            </FormControl>

            <Typography variant="caption" fontWeight={700} sx={{ display: 'block', mb: 1, color: '#6c757d', fontSize: '0.7rem', letterSpacing: 0.5, textTransform: 'uppercase' }}>
              Format
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
              {BARCODE_FORMATS.map(fmt => {
                const selected = barcodeFormat === fmt.value;
                return (
                  <Box
                    key={fmt.value}
                    onClick={() => setBarcodeFormat(fmt.value)}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1.5,
                      px: 1.5,
                      py: 1,
                      borderRadius: 1.5,
                      border: `1.5px solid ${selected ? sidebarColor : '#dee2e6'}`,
                      backgroundColor: selected ? `${sidebarColor}0d` : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                      '&:hover': { borderColor: sidebarColor, backgroundColor: `${sidebarColor}08` },
                    }}
                  >
                    <Box sx={{ color: selected ? sidebarColor : '#adb5bd', display: 'flex' }}>
                      {fmt.icon}
                    </Box>
                    <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: selected ? 600 : 400, color: selected ? sidebarColor : '#495057' }}>
                      {fmt.label}
                    </Typography>
                    {selected && (
                      <Box sx={{ ml: 'auto' }}>
                        <CheckIcon sx={{ fontSize: '0.9rem', color: sidebarColor }} />
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          </Box>
        )}

        {tab === 1 && (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: '0.85rem' }}>
              Insérer le numéro de carte du client
            </Typography>
            <Box
              sx={{
                display: 'flex', alignItems: 'center', gap: 1.5, px: 1.5, py: 1.25,
                borderRadius: 1.5, border: `1.5px solid ${sidebarColor}`, backgroundColor: `${sidebarColor}0d`,
              }}
            >
              <QrCode2Icon sx={{ fontSize: '1.1rem', color: sidebarColor }} />
              <Box>
                <Typography variant="body2" sx={{ fontSize: '0.82rem', fontWeight: 600, color: sidebarColor }}>
                  Code carte
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#6c757d', fontFamily: 'monospace' }}>
                  #NUM_CARTE#
                </Typography>
              </Box>
              <CheckIcon sx={{ fontSize: '0.9rem', color: sidebarColor, ml: 'auto' }} />
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ borderTop: '1px solid #e9ecef', px: 3, py: 1.5, gap: 1 }}>
        <Button variant="outlined" startIcon={<CloseIcon fontSize="small" />} onClick={onClose}
          sx={{ textTransform: 'none', fontSize: '0.82rem', color: '#6c757d', borderColor: '#dee2e6', borderRadius: 1.5, '&:hover': { borderColor: '#adb5bd', backgroundColor: '#f8f9fa' } }}>
          Annuler
        </Button>
        <Button variant="contained" startIcon={<CheckIcon fontSize="small" />} onClick={handleConfirm}
          sx={{ textTransform: 'none', fontSize: '0.82rem', fontWeight: 600, borderRadius: 1.5, backgroundColor: '#28a745', '&:hover': { backgroundColor: '#218838' } }}>
          Confirmer
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default InsertBarcodeDialog;
